import React, { useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { listDashboardYearMonthRows } from '../../utils/dashboardYearMonths';
import { preloadDashboardCalendarArt } from '../../utils/dashboardCalendarArtCache';
import type { AcademicTermDateRange } from '../../utils/dashboardAcademicHolidays';
import {
  clientRectToLayoutRect,
  computeZoomAwareFixedPopupStyle,
  PAGE_ZOOM_CHANGED_EVENT,
} from '../../utils/pageZoom';
import DashboardMonthCalendarCard from './DashboardMonthCalendarCard';

type Props = {
  academicYear: number;
  holidayIsoSet: ReadonlySet<string>;
  holidayNoteByIso?: Readonly<Record<string, string>>;
  termRanges?: ReadonlyArray<AcademicTermDateRange>;
  todayIso?: string;
  selectedIso?: string;
  onSelectDate?: (iso: string) => void;
};

const CALENDAR_ART_INDICES = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14];

type HolidayTip = {
  name: string;
  style: CSSProperties;
};

/** 네비 페이지 줌(html zoom) 반영: GBR 시각좌표 → layout fixed 좌표 */
function holidayTipStyleFromRect(r: DOMRect): CSSProperties {
  const layoutTop = clientRectToLayoutRect(r).top;
  return {
    ...computeZoomAwareFixedPopupStyle({
      anchorRect: r,
      pad: 8,
      gap: 6,
      zIndex: 2200,
      alignX: 'center',
      placement: layoutTop < 48 ? 'below' : 'above',
      estimatedHeight: 40,
    }),
    pointerEvents: 'none',
  };
}

/**
 * 학년도 2월~다음해 2월 달력 — 1행 2월(우정렬) + 올해 CALENDAR 타이틀.
 */
const DashboardYearCalendars: React.FC<Props> = ({
  academicYear,
  holidayIsoSet,
  holidayNoteByIso,
  termRanges,
  todayIso,
  selectedIso,
  onSelectDate,
}) => {
  const rows = useMemo(() => listDashboardYearMonthRows(academicYear), [academicYear]);
  const calendarYear = new Date().getFullYear();
  const [holidayTip, setHolidayTip] = useState<HolidayTip | null>(null);

  const onHolidayHover = useCallback((el: HTMLElement | null, name: string) => {
    if (!el || !name) {
      setHolidayTip(null);
      return;
    }
    setHolidayTip({
      name,
      style: holidayTipStyleFromRect(el.getBoundingClientRect()),
    });
  }, []);

  useEffect(() => {
    preloadDashboardCalendarArt(CALENDAR_ART_INDICES);
  }, []);

  useEffect(() => {
    if (!holidayTip) return;
    const clear = () => setHolidayTip(null);
    window.addEventListener('scroll', clear, true);
    window.addEventListener('resize', clear);
    window.addEventListener(PAGE_ZOOM_CHANGED_EVENT, clear);
    return () => {
      window.removeEventListener('scroll', clear, true);
      window.removeEventListener('resize', clear);
      window.removeEventListener(PAGE_ZOOM_CHANGED_EVENT, clear);
    };
  }, [holidayTip]);

  return (
    <>
      <div className="cp-dash-year-cals" aria-label={`${academicYear}년 2월부터 월별 달력`}>
        {rows.map((row, rowIdx) => (
          <div
            key={`r-${rowIdx}`}
            className={'cp-dash-year-cals-row' + (rowIdx === 0 ? ' is-lead' : '')}
          >
            {rowIdx === 0 ? (
              <div className="cp-dash-year-cals-brand">
                <div className="cp-dash-year-cals-brand-year">{calendarYear}</div>
                <span className="cp-dash-year-cals-brand-label">CALENDAR</span>
                <span className="cp-dash-year-cals-brand-rule" aria-hidden />
              </div>
            ) : null}
            {row.map((m) => (
              <DashboardMonthCalendarCard
                key={`${m.year}-${m.month}`}
                academicYear={academicYear}
                monthRef={m}
                holidayIsoSet={holidayIsoSet}
                holidayNoteByIso={holidayNoteByIso}
                termRanges={termRanges}
                todayIso={todayIso}
                selectedIso={selectedIso}
                onHolidayHover={onHolidayHover}
                onSelectDate={onSelectDate}
              />
            ))}
          </div>
        ))}
      </div>
      {holidayTip
        ? createPortal(
            <div
              className="cp-dash-month-cal-holiday-tip"
              style={holidayTip.style}
              role="tooltip"
            >
              {holidayTip.name}
            </div>,
            document.body
          )
        : null}
    </>
  );
};

export default DashboardYearCalendars;
